import DashboardService from "@/services/DashboardService";

import {
  fulfilledState,
  getDefaultDates,
  pendingState,
  rejectedState,
  resError,
} from "@helpers";
import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";

const buildSearchOptionName = (item, type) => {
  if (type === "manager" || type === "representative") {
    const fullName = [item.name, item.lastname].filter(Boolean).join(" ").trim();
    return fullName || item.title || item.value || "";
  }

  return item.name || item.title || item.value || "";
};

export const getConfirmations = createAsyncThunk(
  "dashboard/getConfirmations",
  async (params, {rejectWithValue}) => {
    try {
      const response = await DashboardService.getConfirmations(params);

      return response.data;
    } catch (err) {
      return rejectWithValue(resError(err));
    }
  },
);

export const getSearchType = createAsyncThunk(
  "dashboard/getSearchType",
  async (params, {rejectWithValue, signal}) => {
    try {
      const response = await DashboardService.getSearchType({...params, signal});

      return {
        type: params.type,
        data: response.data?.data || response.data || [],
      };
    } catch (err) {
      return rejectWithValue(resError(err));
    }
  },
);

export const getMarkersScore = createAsyncThunk(
  "dashboard/getMarkersScore",
  async (params, {rejectWithValue}) => {
    try {
      const response = await DashboardService.getMarkersScore(params);

      return response.data;
    } catch (err) {
      return rejectWithValue(resError(err));
    }
  },
);

const initialState = {
  confirmations: {
    data: [],
    page: 1,
    size: 5,
    totalPages: null,
    totalSucceeded: null,
    totalFailed: null,
    totalInProgress: null,
    sort: {value: "number", direction: "asc"},
    date: {
      from: getDefaultDates().from,
      to: getDefaultDates().to,
    },
    searchType: {
      certification: [],
      manager: [],
      product_line: [],
      latestRequestIdByType: {},
      selectedIds: {
        certification: null,
        manager: null,
        product_line: null,
      },
      errors: null,
      error: "",
      status: "",
    },
    errors: null,
    error: "",
    status: "",
  },
  markersScore: {
    data: [],
    date: {
      from: getDefaultDates().from,
      to: getDefaultDates().to,
    },
    searchType: {
      certification: [],
      manager: [],
      representative: [],
      product_line: [],
      latestRequestIdByType: {},
      selectedIds: {
        certification: null,
        manager: null,
        representative: null,
        product_line: null,
      },
      errors: null,
      error: "",
      status: "",
    },
    errors: null,
    error: "",
    status: "",
  },
  errors: null,
  error: "",
  status: "",
};

const dashboardCertificationSlice = createSlice({
  name: "dashboard-certification",
  initialState,
  reducers: {
    setConfirmationsDate(state, action) {
      state.confirmations.date = action.payload;
    },
    setMarkersScoreDate(state, action) {
      state.markersScore.date = action.payload;
    },
    setStatus(state, action) {
      state.status = action.payload;
    },
    setPage(state, action) {
      state.confirmations.page = Math.max(1, action.payload);
    },
    setSize(state, action) {
      state.confirmations.size = action.payload;
    },
    setSort(state, action) {
      state.confirmations.sort = action.payload;
    },
    setConfirmationsSearchType(state, action) {
      const {type, id} = action.payload;
      if (
        type &&
        Object.prototype.hasOwnProperty.call(
          state.confirmations.searchType.selectedIds,
          type,
        )
      ) {
        state.confirmations.searchType.selectedIds[type] = id;
      }
    },
    setMarkersScoreSearchType(state, action) {
      const {type, id} = action.payload;
      if (
        type &&
        Object.prototype.hasOwnProperty.call(
          state.markersScore.searchType.selectedIds,
          type,
        )
      ) {
        state.markersScore.searchType.selectedIds[type] = id;
      }
    },
    clearCertificationDashboard() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getConfirmations.pending, (state) => {
        pendingState(state.confirmations);
      })
      .addCase(getConfirmations.fulfilled, (state, action) => {
        if (action.payload && action.payload.data) {
          state.confirmations.data = action.payload.data;
          state.confirmations.totalPages = action.payload.total
            ? Math.ceil(action.payload.total / state.confirmations.size)
            : state.confirmations.totalPages;

          if (action.payload.total_succeeded !== undefined) {
            state.confirmations.totalSucceeded = action.payload.total_succeeded;
          }

          if (action.payload.total_failed !== undefined) {
            state.confirmations.totalFailed = action.payload.total_failed;
          }

          if (action.payload.total_in_progress !== undefined) {
            state.confirmations.totalInProgress =
              action.payload.total_in_progress;
          }

          if (
            action.payload.data.length === 0 &&
            action.payload.total_succeeded === undefined
          ) {
            state.confirmations.totalSucceeded = 0;
            state.confirmations.totalFailed = 0;
            state.confirmations.totalInProgress = 0;
            state.confirmations.page = 1;
          }

          fulfilledState(state.confirmations);
        } else {
          state.confirmations.data = [];
          state.confirmations.totalSucceeded = 0;
          state.confirmations.totalFailed = 0;
          state.confirmations.totalInProgress = 0;
          state.confirmations.page = 1;

          rejectedState(state.confirmations, action);
        }
      })
      .addCase(getConfirmations.rejected, (state, action) => {
        rejectedState(state.confirmations, action);
      })

      .addCase(getSearchType.pending, (state, action) => {
        const {section, type} = action.meta.arg || {};
        if (section === "confirmations") {
          if (type) {
            state.confirmations.searchType.latestRequestIdByType[type] =
              action.meta.requestId;
          }
          pendingState(state.confirmations.searchType);
        } else if (section === "markersScore") {
          if (type) {
            state.markersScore.searchType.latestRequestIdByType[type] =
              action.meta.requestId;
          }
          pendingState(state.markersScore.searchType);
        }
      })
      .addCase(getSearchType.fulfilled, (state, action) => {
        const {type, data} = action.payload;
        const {section} = action.meta.arg || {};

        if (section === "confirmations") {
          if (
            type &&
            state.confirmations.searchType.latestRequestIdByType[type] !==
              action.meta.requestId
          ) {
            return;
          }
          if (
            type &&
            Object.prototype.hasOwnProperty.call(
              state.confirmations.searchType,
              type,
            )
          ) {
            state.confirmations.searchType[type] = Array.isArray(data)
              ? data.map((item) => ({
                  id: item.id,
                  name: buildSearchOptionName(item, type),
                  value: item.id,
                }))
              : [];
          }
          fulfilledState(state.confirmations.searchType);
        } else if (section === "markersScore") {
          if (
            type &&
            state.markersScore.searchType.latestRequestIdByType[type] !==
              action.meta.requestId
          ) {
            return;
          }
          if (
            type &&
            Object.prototype.hasOwnProperty.call(
              state.markersScore.searchType,
              type,
            )
          ) {
            state.markersScore.searchType[type] = Array.isArray(data)
              ? data.map((item) => ({
                  id: item.id,
                  name: buildSearchOptionName(item, type),
                  value: item.id,
                }))
              : [];
          }
          fulfilledState(state.markersScore.searchType);
        }
      })
      .addCase(getSearchType.rejected, (state, action) => {
        const {section, type} = action.meta.arg || {};
        if (section === "confirmations") {
          if (
            type &&
            state.confirmations.searchType.latestRequestIdByType[type] !==
              action.meta.requestId
          ) {
            return;
          }
          rejectedState(state.confirmations.searchType, action);
        } else if (section === "markersScore") {
          if (
            type &&
            state.markersScore.searchType.latestRequestIdByType[type] !==
              action.meta.requestId
          ) {
            return;
          }
          rejectedState(state.markersScore.searchType, action);
        }
      })

      .addCase(getMarkersScore.pending, (state) => {
        pendingState(state.markersScore);
      })
      .addCase(getMarkersScore.fulfilled, (state, action) => {
        state.markersScore.data = action.payload.data;
        fulfilledState(state.markersScore);
      })
      .addCase(getMarkersScore.rejected, (state, action) => {
        rejectedState(state.markersScore, action);
      });
  },
});

export const {
  clearCertificationDashboard,
  setConfirmationsDate,
  setMarkersScoreDate,
  setConfirmationsRankingDate,
  setStatus,
  setPage,
  setSize,
  setSort,
  setConfirmationsSearchType,
  setMarkersScoreSearchType,
} = dashboardCertificationSlice.actions;

export default dashboardCertificationSlice.reducer;
